if (typeof ContentBlockModal === 'undefined' && !customElements.get('content-block-modal')) {
  class ContentBlockModal extends HTMLElement {
    constructor() {
      super();

      this.attachShadow({ mode: 'open' });
      const template = document.createElement('template');
      template.innerHTML = `
        <style>
          :host {
            display: none;
            position: fixed;
            inset: 0;
            z-index: 50;
            background: rgba(0, 0, 0, 0.5);
            justify-content: center;
            align-items: center;
          }
          .modal-content {
            position: relative;
            background: white;                
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }
          .close-button {
            position: absolute;
            top: 2.8rem;
            left: 2rem;
            padding: 0.4rem 1.6rem;
            background: #ffffff;
            border: none;
            border-radius: 20px;
            cursor: pointer;
            display: flex;
            gap: 4px;
            align-items: center;
            z-index: 1000;
          }
          .close-button svg {
            rotate: 180deg;
          }
        </style>

        <div class="modal-content">
          <button class="close-button" type="button">
            <span aria-hidden="true">
              <svg class="md:h-[2.1rem] md:w-[2.1rem]" width="21" height="21" viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19" stroke="#183B3F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M12 5L19 12L12 19" stroke="#183B3F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </span>
            <span>Back</span>
          </button>
          <slot></slot>
        </div>
      `;

      this.shadowRoot.appendChild(template.content.cloneNode(true));
      this.modalContent = this.shadowRoot.querySelector('.modal-content');
      this.closeButton = this.shadowRoot.querySelector('.close-button');
      this.openModal = this.openModal.bind(this);
      this.closeModal = this.closeModal.bind(this);
      this.handleAnchorClick = this.handleAnchorClick.bind(this);
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleEditorSelect = this.handleEditorSelect.bind(this);
      this.handleEditorDeselect = this.handleEditorDeselect.bind(this);
      this.closeButton.addEventListener('click', () => this.closeModal());
    }

    connectedCallback() {
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      if (!this.hasAttribute('aria-label') && !this.hasAttribute('aria-labelledby')) {
        const heading = this.querySelector('h1, h2, h3, h4');
        if (heading) {
          if (!heading.id) heading.id = `${this.id || 'modal'}-title`;
          this.setAttribute('aria-labelledby', heading.id);
        }
      }
      document.addEventListener('click', this.handleAnchorClick);
      document.addEventListener('shopify:block:select', this.handleEditorSelect);
      document.addEventListener('shopify:block:deselect', this.handleEditorDeselect);
      this.addEventListener('click', this.handleOutsideClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleAnchorClick);
      document.removeEventListener('shopify:block:select', this.handleEditorSelect);
      document.removeEventListener('shopify:block:deselect', this.handleEditorDeselect);
      document.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('click', this.handleOutsideClick);
    }

    // Theme Editor: selecting the modal's block previews it
    handleEditorSelect(event) {
      if (event.target === this) this.openModal({ focus: false });
    }

    handleEditorDeselect(event) {
      if (event.target === this) this.closeModal({ restoreFocus: false });
    }

    focusableElements() {
      const selector =
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
      const inside = Array.from(this.querySelectorAll(selector)).filter((el) => el.offsetParent !== null);
      return [this.closeButton, ...inside];
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = this.focusableElements();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = this.shadowRoot.activeElement || document.activeElement;
      const outside = active !== this.closeButton && !this.contains(active);

      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    }

    handleAnchorClick(event) {
      const anchor = event.target.closest('a[href^="#modal-"]');
      if (anchor) {
        event.preventDefault();
        const modalId = anchor.getAttribute('href').replace('#modal-', '');

        if (this.id === `modal-window-${modalId}`) {
          this.openModal();
        }
      }
    }

    handleOutsideClick(event) {
      if (event.target === this) {
        this.closeModal();
      }
    }

    openModal({ focus = true } = {}) {
      if (this.style.display === 'flex') return;
      this.returnFocusTo = document.activeElement;
      this.style.display = 'flex';
      document.addEventListener('keydown', this.handleKeydown);
      if (focus) this.closeButton.focus();
    }

    closeModal({ restoreFocus = true } = {}) {
      this.style.display = 'none';
      document.removeEventListener('keydown', this.handleKeydown);
      if (restoreFocus && this.returnFocusTo && typeof this.returnFocusTo.focus === 'function') {
        this.returnFocusTo.focus();
      }
      this.returnFocusTo = null;
    }
  }

  customElements.define('content-block-modal', ContentBlockModal);
}
